import { ChangeEvent, useState } from 'react';
import DialogBox from '@/components/DialogBox';
import { useToastContext } from '@/contexts/toastContext';
import useApiFetch from '@/hooks/useApiFetch';
import useApiSubmit from '@/hooks/useApiSubmit';
import useProfile from '@/hooks/useProfileV2';
import attachmentsService, { SubmitAttachment } from '@/services/attachments';
import { FetchApiIds } from '@/types';
import { PersonType, UploadedCandidateAttachments } from '@/types/api';
import type { LoadingInfo } from './types';
import PersonAttachmentsPageContentComponent from './PersonAttachmentsPageContent.component';

type Props = {
  person?: PersonType;
  readonly?: boolean;
};

const PersonAttachmentsPageContent = ({ person, readonly }: Props): JSX.Element => {
  const [fileToDelete, setFileToDelete] = useState<string>();
  const [deleting, setDeleting] = useState<boolean>(false);
  const toast = useToastContext();
  const [loadingInfo, setLoadingInfo] = useState<LoadingInfo>({
    category: '',
    filesLoadingCount: 0,
  });
  const { profile } = useProfile();

  const getAllCandidateAttachments = useApiFetch<UploadedCandidateAttachments, string>(
    [FetchApiIds.GET_ALL_ATTACHMENTS, person?.id || ''],
    async () => attachmentsService.listAttachments(person?.id || ''),
    { enabled: Boolean(person) && !readonly }
  );

  const getMyAttachments = useApiFetch<UploadedCandidateAttachments, string>(
    [FetchApiIds.GET_MY_ATTACHMENTS],
    async () => attachmentsService.listMyAttachments(),
    { enabled: Boolean(readonly) }
  );

  const resultAttachments = readonly ? getMyAttachments : getAllCandidateAttachments;

  const handleDeleteAttachment = (fileId: string): void => {
    setFileToDelete(fileId);
  };

  const handleDeleteConfirm = async (fileId?: string): Promise<void> => {
    if (!fileId) return;

    try {
      setDeleting(true);
      await attachmentsService.deleteAttachment(person?.id || '', fileId);
      setFileToDelete(undefined);
      getAllCandidateAttachments.refetch();
      toast.setMessage('Attachment removed successfully!');
      toast.setSeverity('success');
      toast.showToast();
    } catch (err) {
      toast.setMessage('There was an error removing the attachment. Please, try again.');
      toast.setSeverity('error');
      toast.showToast();
    } finally {
      setDeleting(false);
    }
  };

  const createAttachmentMutation = useApiSubmit<void, SubmitAttachment>(
    attachmentsService.submitAttachment
  );

  const handleSelectFile = async (
    e: ChangeEvent<HTMLInputElement>,
    category: string
  ): Promise<void> => {
    e.preventDefault();
    const selectedFiles = e.target.files;

    if (!selectedFiles) {
      return;
    }

    try {
      const payload = new FormData();
      [...selectedFiles].forEach((file) => payload.append('files', file));
      payload.append('category', category);
      payload.append('attachedBy', profile.id || '');
      setLoadingInfo({
        category,
        filesLoadingCount: [...selectedFiles].length,
      });
      await createAttachmentMutation.mutateAsync({
        payload,
        candidateId: person?.id || '',
      });
      getAllCandidateAttachments.refetch();
      toast.setMessage('Upload complete!');
      toast.setSeverity('success');
      toast.showToast();

      e.target.value = '';
    } catch (error: any) {
      toast.setMessage('Error uploading file! Please, try again.');
      toast.setSeverity('error');
      toast.showToast();
      e.target.value = '';
    } finally {
      setLoadingInfo((info) => ({ ...info, filesLoadingCount: 0 }));
    }
  };

  return (
    <>
      <PersonAttachmentsPageContentComponent
        attachments={resultAttachments.data}
        onDeleteAttachment={handleDeleteAttachment}
        onSelectFile={handleSelectFile}
        uploadingInfo={loadingInfo}
        readonly={readonly}
      />
      <DialogBox
        isOpen={Boolean(fileToDelete)}
        onCancelHandler={() => setFileToDelete(undefined)}
        onConfirmHandler={() => handleDeleteConfirm(fileToDelete)}
        isSubmitting={deleting}
      />
    </>
  );
};

export default PersonAttachmentsPageContent;
